import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URLS, obtenerEntidades } from '../../Services/servicioURL';
import CryptoJS from 'crypto-js';
import { useRef } from 'react';

interface Licencia {
    id: number;
    tipo: string;
    clave: string;
    descripcion: string;
    cveemp: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    version: string;
    observaciones: string;
    empleado?: {
        cveemp: string;
        nomemp: string;
        sucursal: string;
    };
    // Campos desencriptados
    claveDesencriptada?: string;
    descripcionDesencriptada?: string;
    observacionesDesencriptadas?: string;
}

interface Empleado {
    cveemp: string;
    nomemp: string;
    sucursal: string;
    departamento: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

const ConsultorLicencias: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<Licencia[]>([]);
    const [filteredData, setFilteredData] = useState<Licencia[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [empleadosMap, setEmpleadosMap] = useState<Record<string, string>>({});
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    
    // Estados para los filtros
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [selectedSucursal, setSelectedSucursal] = useState<string | null>(null);
    const [selectedDepartamento, setSelectedDepartamento] = useState<string | null>(null);
    const [selectedEstatus, setSelectedEstatus] = useState<string | null>(null);
    const [selectedTipo, setSelectedTipo] = useState<string | null>(null);

    const toast = useRef<Toast>(null);

    // Clave maestra para desencriptación (debe coincidir con la del componente original)
    const MASTER_KEY = 'BabatsaLicencias2024!@#$%^&*()_+SecureKey';

    const estatusOptions = [
        { label: 'ACTIVA', value: 'ACTIVA' },
        { label: 'INACTIVA', value: 'INACTIVA' },
        { label: 'VENCIDA', value: 'VENCIDA' },
        { label: 'REVOCADA', value: 'REVOCADA' }
    ];

    const tiposLicencia = [
        { label: 'Windows 10 Pro', value: 'Windows 10 Pro' },
        { label: 'Windows 11 Pro', value: 'Windows 11 Pro' },
        { label: 'Microsoft Office 365', value: 'Microsoft Office 365' },
        { label: 'Microsoft Office 2021', value: 'Microsoft Office 2021' },
        { label: 'Antivirus Kaspersky', value: 'Antivirus Kaspersky' },
        { label: 'Antivirus Norton', value: 'Antivirus Norton' },
        { label: 'Adobe Acrobat Pro', value: 'Adobe Acrobat Pro' },
        { label: 'AutoCAD', value: 'AutoCAD' },
        { label: 'Otro', value: 'Otro' }
    ];

    // 🔐 VALIDACIÓN DE ACCESO ESPECIAL
    const validateSystemPermissions = (): boolean => {
        try {
            const userIdString = localStorage.getItem('idUsuario');
            if (!userIdString) {
                return false;
            }
            
            const userId = parseInt(userIdString, 10);
            const authorizedProfiles = [1, 3, 4, 5, 134];
            
            return authorizedProfiles.includes(userId);
        } catch (error) {
            console.error('Error en validación de permisos:', error);
            return false;
        }
    };

    // Efecto para validar acceso al cargar el componente
    useEffect(() => {
        const checkAccess = () => {
            const hasPermission = validateSystemPermissions();
            
            if (!hasPermission) {
                // Mostrar mensaje de acceso denegado antes de redirigir
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Acceso Restringido',
                    detail: 'No tienes permisos para acceder a esta sección',
                    life: 2000
                });
                
                // Redirigir después de un breve delay
                setTimeout(() => {
                    navigate('/licencias');
                }, 2000);
                
                return;
            }
            
            setHasAccess(true);
        };

        checkAccess();
    }, [navigate]);

    // 🔓 FUNCIONES DE DESENCRIPTACIÓN GOST-CBC
    const desencriptarGOST = (textoEncriptado: string, clave: string = MASTER_KEY): string => {
        try {
            if (!textoEncriptado || textoEncriptado.trim() === '') {
                return '';
            }

            const key = CryptoJS.SHA256(clave);
            const combined = CryptoJS.enc.Base64.parse(textoEncriptado);
            
            // Extraer IV (primeros 16 bytes) y datos encriptados
            const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));
            const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(4));
            
            const decrypted = CryptoJS.AES.decrypt(
                CryptoJS.lib.CipherParams.create({ ciphertext: encrypted }),
                key,
                {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7
                }
            );
            
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error('Error en desencriptación:', error);
            return textoEncriptado; // Fallback
        }
    };

    // 🔓 FUNCIÓN PARA DESENCRIPTAR DATOS SENSIBLES
    const desencriptarDatosSensibles = (licencia: Licencia): Licencia => {
        return {
            ...licencia,
            claveDesencriptada: desencriptarGOST(licencia.clave),
            descripcionDesencriptada: licencia.descripcion ? desencriptarGOST(licencia.descripcion) : '',
            observacionesDesencriptadas: licencia.observaciones ? desencriptarGOST(licencia.observaciones) : ''
        };
    };

    // 🔧 FUNCIÓN PARA ASEGURAR QUE SIEMPRE SEA UN ARRAY
    const ensureArray = (data: any): any[] => {
        if (Array.isArray(data)) {
            return data;
        }
        if (data && typeof data === 'object' && data.success && Array.isArray(data.data)) {
            return data.data;
        }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        console.warn('Datos recibidos no son un array:', data);
        return [];
    };

    // Función para crear fecha local sin problemas de zona horaria
    const createLocalDate = (dateString: string): Date => {
        if (!dateString) return new Date();
        
        const dateParts = dateString.split('T')[0].split('-');
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1;
            const day = parseInt(dateParts[2]);
            return new Date(year, month, day);
        }
        
        return new Date(dateString);
    };

    // Función para formatear fecha sin problemas de zona horaria
    const formatLocalDate = (date: Date): string => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    // Cargar empleados y crear un mapa de cveemp -> nombre
    useEffect(() => {
        if (!hasAccess) return;

        const fetchEmpleados = async () => {
            try {
                const result = await obtenerEntidades(API_URLS.empleados.obtener);
                const empleadosArray = ensureArray(result);

                const map = empleadosArray.reduce((acc, emp) => {
                    acc[emp.cveemp.trim()] = emp.nomemp.trim();
                    return acc;
                }, {} as Record<string, string>);
                setEmpleadosMap(map);
            } catch (error) {
                console.error("Error al cargar empleados:", error);
            }
        };
        fetchEmpleados();
    }, [hasAccess]);

    // Cargar sucursales para el dropdown
    useEffect(() => {
        if (!hasAccess) return;

        const fetchSucursales = async () => {
            try {
                const result = await obtenerEntidades(API_URLS.sucursales.obtener);
                const sucursalesArray = ensureArray(result);
                setSucursales(sucursalesArray);
            } catch (error) {
                console.error("Error al cargar sucursales:", error);
            }
        };
        fetchSucursales();
    }, [hasAccess]);

    // Cargar departamentos para el dropdown
    useEffect(() => {
        if (!hasAccess) return;

        const fetchDepartamentos = async () => {
            try {
                const result = await obtenerEntidades(API_URLS.departamentos.obtener);
                const departamentosArray = ensureArray(result);
                setDepartamentos(departamentosArray);
            } catch (error) {
                console.error("Error al cargar departamentos:", error);
            }
        };
        fetchDepartamentos();
    }, [hasAccess]);

    // Cargar licencias y desencriptarlas
    useEffect(() => {
        if (!hasAccess) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await obtenerEntidades(API_URLS.licencias.obtener);
                const licenciasArray = ensureArray(result);

                // Desencriptar datos sensibles y enriquecer con información de empleados
                const licenciasDesencriptadas = licenciasArray.map(licencia => {
                    const licenciaDesencriptada = desencriptarDatosSensibles(licencia);
                    const nombreEmpleado = empleadosMap[licencia.cveemp?.trim()] || licencia.cveemp;
                    
                    return {
                        ...licenciaDesencriptada,
                        empleado: {
                            cveemp: licencia.cveemp,
                            nomemp: nombreEmpleado,
                            sucursal: licencia.sucursal
                        }
                    };
                });

                setData(licenciasDesencriptadas);
                setFilteredData(licenciasDesencriptadas);

                if (licenciasDesencriptadas.length === 0) {
                    toast.current?.show({
                        severity: 'info',
                        summary: 'Sin datos',
                        detail: 'No se encontraron licencias registradas',
                        life: 3000
                    });
                } else {
                    toast.current?.show({
                        severity: 'success',
                        summary: '🔓 Desencriptación exitosa',
                        detail: `Se desencriptaron ${licenciasDesencriptadas.length} licencias`,
                        life: 3000
                    });
                }
            } catch (error) {
                console.error("Error al cargar licencias:", error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar las licencias',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        if (Object.keys(empleadosMap).length > 0) {
            fetchData();
        }
    }, [empleadosMap, hasAccess]);

    // Filtrar por término de búsqueda global + filtros
    useEffect(() => {
        if (!hasAccess) return;

        let filtered = [...data];

        // Aplicar filtro de sucursal
        if (selectedSucursal !== null) {
            filtered = filtered.filter(d => d.sucursal === selectedSucursal);
        }

        // Aplicar filtro de departamento
        if (selectedDepartamento !== null) {
            filtered = filtered.filter(d => d.departamento === selectedDepartamento);
        }

        // Aplicar filtro de estatus
        if (selectedEstatus !== null) {
            filtered = filtered.filter(d => d.estatus === selectedEstatus);
        }

        // Aplicar filtro de tipo
        if (selectedTipo !== null) {
            filtered = filtered.filter(d => d.tipo === selectedTipo);
        }

        // Aplicar filtro de búsqueda global (incluyendo datos desencriptados)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(d =>
                d.tipo?.toLowerCase().includes(term) ||
                d.claveDesencriptada?.toLowerCase().includes(term) ||
                d.descripcionDesencriptada?.toLowerCase().includes(term) ||
                d.empleado?.nomemp?.toLowerCase().includes(term) ||
                d.cveemp?.toLowerCase().includes(term) ||
                d.sucursal?.toLowerCase().includes(term) ||
                d.departamento?.toLowerCase().includes(term) ||
                d.estatus?.toLowerCase().includes(term) ||
                d.version?.toLowerCase().includes(term) ||
                d.observacionesDesencriptadas?.toLowerCase().includes(term)
            );
        }

        setFilteredData(filtered);
    }, [searchTerm, data, selectedSucursal, selectedDepartamento, selectedEstatus, selectedTipo, hasAccess]);

    // Función para limpiar filtros
    const clearFilters = () => {
        setSelectedSucursal(null);
        setSelectedDepartamento(null);
        setSelectedEstatus(null);
        setSelectedTipo(null);
        setSearchTerm('');
    };

    // Template para mostrar el estatus con color
    const estatusBodyTemplate = (rowData: Licencia) => {
        const getEstatusColor = (estatus: string) => {
            switch (estatus) {
                case 'ACTIVA': return 'success';
                case 'INACTIVA': return 'secondary';
                case 'VENCIDA': return 'danger';
                case 'REVOCADA': return 'warning';
                default: return 'secondary';
            }
        };

        return (
            <div style={{ textAlign: 'center' }}>
                <Badge value={rowData.estatus} severity={getEstatusColor(rowData.estatus)} />
            </div>
        );
    };

    // Template para mostrar la clave desencriptada
    const claveBodyTemplate = (rowData: Licencia) => {
        return (
            <div style={{ 
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.2',
                padding: '4px 0',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '4px',
                border: '1px solid #bbf7d0'
            }}>
                🔓 {rowData.claveDesencriptada || 'N/A'}
            </div>
        );
    };

    // Template para descripción desencriptada
    const descripcionBodyTemplate = (rowData: Licencia) => {
        return (
            <div style={{ 
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.2',
                padding: '4px 0'
            }}>
                {rowData.descripcionDesencriptada || '-'}
            </div>
        );
    };

    // Template para observaciones desencriptadas
    const observacionesBodyTemplate = (rowData: Licencia) => {
        return (
            <div style={{ 
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.2',
                padding: '4px 0'
            }}>
                {rowData.observacionesDesencriptadas || '-'}
            </div>
        );
    };

    // Template para empleado
    const empleadoBodyTemplate = (rowData: Licencia) => {
        return (
            <div>
                <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {rowData.empleado?.nomemp || rowData.cveemp}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {rowData.cveemp}
                </div>
            </div>
        );
    };

    // Exportar a CSV (Excel)
    const exportToExcel = () => {
        const headers = ['ID', 'Tipo', 'Clave Desencriptada', 'Descripción', 'Empleado', 'Sucursal', 'Departamento', 'Estatus', 'Versión', 'Observaciones'];
        let csvContent = [headers.join(',')];
        filteredData.forEach(licencia => {
            csvContent.push([
                licencia.id,
                `"${licencia.tipo || ''}"`,
                `"${licencia.claveDesencriptada || ''}"`,
                `"${licencia.descripcionDesencriptada || ''}"`,
                `"${licencia.empleado?.nomemp || licencia.cveemp || ''}"`,
                `"${licencia.sucursal || ''}"`,
                `"${licencia.departamento || ''}"`,
                `"${licencia.estatus || ''}"`,
                `"${licencia.version || ''}"`,
                `"${licencia.observacionesDesencriptadas || ''}"`
            ].join(','));
        });
        const blob = new Blob([`\uFEFF${csvContent.join('\n')}`], {
            type: 'text/csv;charset=utf-8'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Licencias-Desencriptadas-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    // Función para exportar a PDF
    const exportToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            
            // Agregar la imagen del membretado
            const img = new Image();
            img.src = '/dist/img/bbtt2.png';
            
            img.onload = function() {
                doc.addImage(img, 'PNG', 40, 20, 65, 65);
                
                doc.setProperties({
                    title: 'Reporte de Licencias Desencriptadas BBT-SIS',
                    subject: 'Licencias de software',
                    author: 'Sistema de Inventarios',
                    keywords: 'licencias, software, reporte, pdf',
                    creator: 'Sistema de Inventarios'
                });

                doc.setFontSize(18);
                doc.setTextColor(40);
                doc.text('🔓 Reporte de Licencias Desencriptadas BBT-SIS', 150, 40);
                
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(`Generado el: ${formatLocalDate(new Date())}`, 150, 60);
                doc.text(`Total de registros: ${filteredData.length}`, 150, 75);
                
                let yPosition = 90;
                if (selectedSucursal !== null) {
                    doc.text(`Filtro Sucursal: ${selectedSucursal}`, 150, yPosition);
                    yPosition += 15;
                }
                if (selectedDepartamento !== null) {
                    doc.text(`Filtro Departamento: ${selectedDepartamento}`, 150, yPosition);
                    yPosition += 15;
                }
                if (selectedEstatus !== null) {
                    doc.text(`Filtro Estatus: ${selectedEstatus}`, 150, yPosition);
                    yPosition += 15;
                }
                
                const tableData = filteredData.map(licencia => [
                    licencia.id.toString(),
                    licencia.tipo || '',
                    licencia.claveDesencriptada || '',
                    (licencia.descripcionDesencriptada || '').substring(0, 30) + (licencia.descripcionDesencriptada && licencia.descripcionDesencriptada.length > 30 ? '...' : ''),
                    licencia.empleado?.nomemp || licencia.cveemp || '',
                    licencia.sucursal || '',
                    licencia.departamento || '',
                    licencia.estatus || '',
                    licencia.version || '',
                    (licencia.observacionesDesencriptadas || '').substring(0, 30) + (licencia.observacionesDesencriptadas && licencia.observacionesDesencriptadas.length > 30 ? '...' : '')
                ]);

                autoTable(doc, {
                    head: [['ID', 'Tipo', '🔓 Clave', 'Descripción', 'Empleado', 'Sucursal', 'Depto.', 'Estatus', 'Versión', 'Observaciones']],
                    body: tableData,
                    startY: Math.max(yPosition, 105),
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 7,
                        cellPadding: 2,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 8
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 30, halign: 'center' },
                        1: { cellWidth: 80, halign: 'left' },
                        2: { cellWidth: 100, halign: 'left' },
                        3: { cellWidth: 'auto', halign: 'left' },
                        4: { cellWidth: 80, halign: 'center' },
                        5: { cellWidth: 60, halign: 'center' },
                        6: { cellWidth: 50, halign: 'center' },
                        7: { cellWidth: 50, halign: 'center' },
                        8: { cellWidth: 40, halign: 'center' },
                        9: { cellWidth: 'auto', halign: 'left' }
                    },
                    didDrawPage: function (data: { pageNumber: any; settings: { margin: { left: number; }; }; }) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(
                            `Página ${currentPage} de ${pageCount}`,
                            data.settings.margin.left,
                            doc.internal.pageSize.height - 20
                        );
                    }
                });
                
                doc.save(`licencias_desencriptadas_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
            
            img.onerror = function() {
                console.error('Error al cargar la imagen del membretado');
                // Continuar sin imagen
                doc.setFontSize(18);
                doc.setTextColor(40);
                doc.text('🔓 Reporte de Licencias Desencriptadas BBT-SIS', 40, 40);
                
                // ... resto del código sin imagen
                doc.save(`licencias_desencriptadas_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Ocurrió un error al generar el PDF',
                life: 3000
            });
        }
    };

    // Si no tiene acceso, mostrar mensaje de carga mientras redirige
    if (!hasAccess) {
        return (
            <div>
                <Navbar />
                <Sidebar />
                <Toast ref={toast} />
                
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '16px',
                        padding: '40px',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                        maxWidth: '500px'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔐</div>
                        <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Acceso Restringido</h2>
                        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                            Verificando permisos de usuario...
                        </p>
                        <div style={{
                            width: '100%',
                            height: '4px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '2px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#3b82f6',
                                animation: 'loading 1.5s infinite'
                            }}></div>
                        </div>
                        <style>
                            {`
                                @keyframes loading {
                                    0% { transform: translateX(-100%); }
                                    100% { transform: translateX(100%); }
                                }
                            `}
                        </style>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Header principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            🔓 Consultor de Licencias Desencriptadas
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-unlock"></i>
                            {loading ? 'DESENCRIPTANDO...' : 'DATOS DESENCRIPTADOS'}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #10b981, #3b82f6)', borderRadius: '1px' }}></div>
                </div>

                {/* Panel de filtros */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{ 
                        margin: '0 0 16px 0', 
                        color: '#374151', 
                        fontSize: '1.2rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        🎛️ Filtros y Búsqueda
                    </h3>
                    
                    {/* Filtros principales */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '16px', 
                        marginBottom: '20px'
                    }}>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                💻 Tipo de Licencia
                            </label>
                            <Dropdown
                                value={selectedTipo}
                                options={tiposLicencia}
                                onChange={(e) => setSelectedTipo(e.value)}
                                placeholder="Todos los tipos"
                                showClear
                                style={{ width: '100%', height: '40px' }}
                            />
                        </div>

                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                🏢 Sucursal
                            </label>
                            <Dropdown
                                value={selectedSucursal}
                                options={sucursales.map(s => ({ label: s.nombre, value: s.nombre }))}
                                onChange={(e) => setSelectedSucursal(e.value)}
                                placeholder="Todas las sucursales"
                                showClear
                                style={{ width: '100%', height: '40px' }}
                            />
                        </div>
                        
                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                🏬 Departamento
                            </label>
                            <Dropdown
                                value={selectedDepartamento}
                                options={departamentos.map(d => ({ label: d.nombre, value: d.nombre }))}
                                onChange={(e) => setSelectedDepartamento(e.value)}
                                placeholder="Todos los departamentos"
                                showClear
                                style={{ width: '100%', height: '40px' }}
                            />
                        </div>

                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                📊 Estatus
                            </label>
                            <Dropdown
                                value={selectedEstatus}
                                options={estatusOptions}
                                onChange={(e) => setSelectedEstatus(e.value)}
                                placeholder="Todos los estatus"
                                showClear
                                style={{ width: '100%', height: '40px' }}
                            />
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                🔍 Búsqueda Global (incluye datos desencriptados)
                            </label>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    placeholder="Buscar en todos los campos (incluyendo claves desencriptadas)..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Barra de acciones */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Button
                                icon="pi pi-filter-slash"
                                label="Limpiar Filtros"
                                size="small"
                                severity="secondary"
                                outlined
                                onClick={clearFilters}
                                style={{ 
                                    height: '36px',
                                    borderRadius: '8px'
                                }}
                            />
                            
                            {/* Contador de registros */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: '#f0fdf4',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#374151',
                                border: '1px solid #bbf7d0'
                            }}>
                                🔓 <span style={{ color: '#10b981' }}>{filteredData.length}</span> de <span style={{ color: '#3b82f6' }}>{data.length}</span> licencias desencriptadas
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button 
                                icon="pi pi-file-excel" 
                                label="Excel"
                                size="small"
                                severity="success"
                                onClick={exportToExcel}
                                disabled={filteredData.length === 0}
                                style={{ 
                                    height: '36px',
                                    borderRadius: '8px',
                                    fontWeight: '600'
                                }}
                            />
                            <Button 
                                icon="pi pi-file-pdf" 
                                label="PDF"
                                size="small"
                                severity="danger"
                                onClick={exportToPDF}
                                disabled={filteredData.length === 0}
                                style={{ 
                                    height: '36px',
                                    borderRadius: '8px',
                                    fontWeight: '600'
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Información de seguridad */}
                <div style={{ 
                    backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{ fontSize: '1.5rem' }}>🔓</div>
                    <div>
                        <strong style={{ color: '#047857' }}>Datos Desencriptados:</strong>
                        <span style={{ color: '#374151', marginLeft: '8px' }}>
                            Las claves, descripciones y observaciones han sido desencriptadas usando GOST-CBC para su visualización. Mantenga la confidencialidad de esta información.
                        </span>
                    </div>
                </div>

                {/* Tabla principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <DataTable 
                        value={filteredData} 
                        responsiveLayout="scroll" 
                        stripedRows 
                        size="small"
                        scrollable
                        scrollHeight="600px"
                        emptyMessage={loading ? "🔄 Cargando y desencriptando datos..." : "📭 No hay registros que mostrar"}
                        removableSort
                        sortMode="multiple"
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="📄 Mostrando {first} a {last} de {totalRecords} licencias"
                        rowHover
                        style={{ 
                            fontSize: '0.85rem',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                    >
                        <Column 
                            field="id" 
                            header="ID" 
                            sortable
                            style={{ 
                                width: '60px', 
                                padding: '0.4rem',
                                textAlign: 'center'
                            }}
                        />
                        <Column 
                            field="tipo" 
                            header="💻 Tipo" 
                            sortable
                            style={{ 
                                width: '120px', 
                                padding: '0.4rem'
                            }}
                        />
                        <Column 
                            field="claveDesencriptada" 
                            header="🔓 Clave Desencriptada" 
                            sortable
                            body={claveBodyTemplate}
                            style={{ 
                                width: '200px', 
                                padding: '0.4rem',
                                verticalAlign: 'top'
                            }}
                        />
                        <Column 
                            field="descripcionDesencriptada" 
                            header="📝 Descripción" 
                            sortable
                            body={descripcionBodyTemplate}
                            style={{ 
                                width: '150px', 
                                padding: '0.4rem',
                                verticalAlign: 'top'
                            }}
                        />
                        <Column 
                            field="empleado.nomemp" 
                            header="👤 Empleado" 
                            sortable
                            body={empleadoBodyTemplate}
                            style={{ 
                                width: '140px', 
                                padding: '0.4rem'
                            }}
                        />
                        <Column 
                            field="sucursal" 
                            header="🏢 Sucursal" 
                            sortable
                            style={{ 
                                width: '100px', 
                                padding: '0.4rem', 
                                textAlign: 'center'
                            }}
                        />
                        <Column 
                            field="departamento" 
                            header="🏬 Depto." 
                            sortable
                            style={{ 
                                width: '100px', 
                                padding: '0.4rem',
                                textAlign: 'center'
                            }}
                        />
                        <Column 
                            field="estatus" 
                            header="📊 Estatus" 
                            sortable
                            body={estatusBodyTemplate}
                            style={{ 
                                width: '90px', 
                                padding: '0.4rem',
                                textAlign: 'center'
                            }}
                        />
                        <Column 
                            field="version" 
                            header="🔢 Versión" 
                            sortable
                            style={{ 
                                width: '80px', 
                                padding: '0.4rem',
                                textAlign: 'center'
                            }}
                        />
                        <Column 
                            field="observacionesDesencriptadas" 
                            header="📋 Observaciones" 
                            sortable
                            body={observacionesBodyTemplate}
                            style={{ 
                                width: '150px', 
                                padding: '0.4rem',
                                verticalAlign: 'top'
                            }}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default ConsultorLicencias;